import { Appear, Buttons, Button, Divider, Flex, Text, Toggle } from '@bitrise/bitkit';
import { FirestoreDetection } from '@trace/types';
import { firestore } from 'firebase/app';
import { DateTime, Interval } from 'luxon';
import React, { useContext, useMemo, FunctionComponent } from 'react';
import { useDocumentDataOnce } from 'react-firebase-hooks/firestore';

import dataAtIds from '../../dataAtIds.json';
import { AuthContextTrace } from '../Authenticate/AuthenticateTrace';
import OnboardingProgress from '../Onboarding/Progress';
import SDKIndicator from '../SDKIndicator/SDKIndicator';
import { URLStateContext } from '../URLState/URLState';
import './AppSidebarPanel.css';

const AppSidebarPanel: FunctionComponent = () => {
  const { demo, updateUrlState } = useContext(URLStateContext);
  const {
    data: { appId },
  } = useContext(AuthContextTrace);
  const [detection] = useDocumentDataOnce<FirestoreDetection>(
    firestore().doc(`/detections/${appId}`),
  );
  const firstSeen = detection && detection.firstSeen;

  const firstSeenInterval = useMemo(
    () =>
      !firstSeen
        ? null
        : Interval.fromDateTimes(DateTime.fromJSDate(firstSeen.toDate()), DateTime.local()),
    [firstSeen],
  );

  const hasInstalledViaStep =
    detection &&
    Array.isArray(detection.installation) &&
    detection.installation.includes('Trace step');

  const handleDemoProjectChange = () => {
    updateUrlState({ demo: !demo });
  };

  return (
    <Appear animation="FadeSlideUp" delay={500} duration="slow">
      <Flex padding="x3">
        <Flex
          borderColor="grape-2"
          borderRadius="x2"
          borderWidth="x1"
          className="AppSidebarPanel"
          paddingVertical="x4"
        >
          <Flex margin="x4" paddingHorizontal="x6">
            <Toggle
              checked={demo}
              data-trace-at-id={dataAtIds.DemoToggle}
              onChange={handleDemoProjectChange}
            >
              <Text textColor="grape-1">Demo data</Text>
            </Toggle>

            <Divider color="grape-2" margin="x3" width="1px" />

            <SDKIndicator />

            {(!firstSeen || (firstSeenInterval && firstSeenInterval.length('hours') < 24)) && (
              <>
                <Divider color="grape-2" margin="x3" width="1px" />
                <OnboardingProgress />
              </>
            )}

            {!hasInstalledViaStep &&
              !!firstSeenInterval &&
              firstSeenInterval.length('hours') >= 24 && (
                <>
                  <Divider color="grape-2" margin="x3" width="1px" />
                  <Flex padding="x3">
                    <Text align="middle" config="8">
                      Add Trace to your automated workflow.
                    </Text>
                    <Buttons alignChildrenHorizontal="middle" margin="x3">
                      <Button
                        Component="a"
                        href="https://devcenter.bitrise.io/monitoring/trace-index/#installing-trace-on-bitrise"
                        size="small"
                      >
                        Automate your workflow
                      </Button>
                    </Buttons>
                  </Flex>
                </>
              )}
          </Flex>
        </Flex>
      </Flex>
    </Appear>
  );
};

export default AppSidebarPanel;
